// ============================================================================
// Margin Extremes Widget — top 5 and bottom 5 active facilities by LABOR
// margin %. Flight-risk review on the left (lowest), "what makes these
// healthy?" on the right (highest).
//
// LABOR MARGIN definition (per Ben, locked 2026-05-19):
//   (SUM(billing_rate) − SUM(sub_rate_monthly)) / SUM(billing_rate)
//   across active service_assignments rows for the facility.
//
// Excluded from the ranking (counted as data-quality, not as outliers):
//   * facilities with zero active assignments
//   * facilities where billing_rate sum = 0 (assignment rate missing)
//   * facilities where sub_rate_monthly sum = 0 (sub rate missing)
// The excluded count surfaces in the widget header so the cleanup queue
// is visible without polluting the extremes.
//
// Filter: billing_total > $500 (assignment-level) to drop trial / pass-
// through / edge-case rows that would dominate either list.
//
// Click any row → jump to that facility's detail in Account Management.
// ============================================================================

import { Link } from 'react-router-dom';
import type { MarginExtreme, MarginExtremes } from './use-dashboard-data';
import { WidgetCard, WidgetSkeleton, fmtCompactCurrency } from './widget-card';

interface Props {
  data: MarginExtremes | null;
  loading: boolean;
  /** # of active facilities excluded due to data-quality issues. */
  dataQualityExcludedCount?: number;
}

export function MarginExtremesWidget({ data, loading, dataQualityExcludedCount }: Props) {
  if (loading && !data) {
    return (
      <WidgetCard title="Margin extremes" minHeight={320}>
        <WidgetSkeleton lines={8} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Margin extremes" minHeight={320}>
        <p className="text-sm text-tcs-text-muted">
          No margin data available.
        </p>
      </WidgetCard>
    );
  }

  const dqNote =
    dataQualityExcludedCount && dataQualityExcludedCount > 0
      ? ` · ${dataQualityExcludedCount} excluded for missing rates`
      : '';
  return (
    <WidgetCard
      title="Labor margin extremes"
      subtitle={`Active sites · assignment-level billing > $500 · flight-risk review${dqNote}`}
      minHeight={320}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wide text-rag-red">
            Lowest 5 margin
          </h3>
          {data.lowest.length === 0 ? (
            <p className="text-xs text-tcs-text-muted">No data.</p>
          ) : (
            <ul className="divide-y divide-tcs-border">
              {data.lowest.map((s) => (
                <ExtremeRow key={s.facility_id} site={s} tone="bad" />
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wide text-st-ok-fg">
            Highest 5 margin
          </h3>
          {data.highest.length === 0 ? (
            <p className="text-xs text-tcs-text-muted">No data.</p>
          ) : (
            <ul className="divide-y divide-tcs-border">
              {data.highest.map((s) => (
                <ExtremeRow key={s.facility_id} site={s} tone="good" />
              ))}
            </ul>
          )}
        </div>
      </div>
    </WidgetCard>
  );
}

function ExtremeRow({
  site,
  tone,
}: {
  site: MarginExtreme;
  tone: 'good' | 'bad';
}) {
  const toneClass = tone === 'good' ? 'text-st-ok-fg' : 'text-rag-red';
  return (
    <li className="py-1.5 text-sm">
      <Link
        to={`/account-management/facilities/${site.facility_id}`}
        className="block hover:bg-tcs-bg-muted hover:rounded -mx-1 px-1"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-tcs-text">
              {site.facility_name}
            </div>
            <div className="text-[10px] text-tcs-text-muted truncate">
              {site.account_name ? `${site.account_name} · ` : ''}
              {SEGMENT_LABEL[site.segment] ?? site.segment}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div
              className={`text-sm font-semibold tabular-nums ${toneClass}`}
            >
              {(site.margin_pct * 100).toFixed(1)}%
            </div>
            <div className="text-[10px] tabular-nums text-tcs-text-muted">
              {fmtCompactCurrency(site.arr)}/yr
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}

const SEGMENT_LABEL: Record<string, string> = {
  Hospital: 'Hospital',
  'ASC-Active': 'ASC',
  'ASC-Pending': 'ASC (pending)',
  'Dialysis-DaVita-KC': 'DaVita · KC',
  'Dialysis-DaVita-OOM': 'DaVita · OOM',
  'Dialysis-Fresenius': 'Fresenius',
  'Dialysis-PureLife': 'Pure Life',
  'Dialysis-Other': 'Dialysis',
  MOB: 'MOB',
  Other: 'Other',
};
